import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type EpicStatus = "active" | "done" | "archived";
export type SprintStatus = "planned" | "active" | "completed";
export type BacklogItemType = "task" | "bug" | "story";
export type BacklogItemStatus = "backlog" | "todo" | "in_progress" | "review" | "done";

export interface ProjectEpic {
  id: string;
  client_project_id: string;
  title: string;
  description: string | null;
  color: string | null;
  status: EpicStatus;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectSprint {
  id: string;
  client_project_id: string;
  name: string;
  goal: string | null;
  status: SprintStatus;
  start_date: string | null;
  end_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectBacklogItem {
  id: string;
  client_project_id: string;
  epic_id: string | null;
  sprint_id: string | null;
  title: string;
  description: string | null;
  item_type: BacklogItemType;
  status: BacklogItemStatus;
  story_points: number | null;
  priority: number;
  assigned_to: string | null;
  due_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectSubtask {
  id: string;
  backlog_item_id: string;
  title: string;
  is_done: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Epics ──────────────────────────────────────────────────────────────────

export function useEpics(clientProjectId: string | undefined) {
  return useQuery({
    queryKey: ["project-epics", clientProjectId],
    queryFn: async () => {
      if (!clientProjectId) return [];
      const { data, error } = await supabase
        .from("project_epics" as never)
        .select("*")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as unknown as ProjectEpic[];
    },
    enabled: !!clientProjectId,
  });
}

export function useCreateEpic(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { title: string; description?: string; color?: string }) => {
      const { data: existing } = await supabase
        .from("project_epics" as never)
        .select("display_order")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: false })
        .limit(1);
      const maxOrder =
        (existing as unknown as { display_order: number }[])?.[0]?.display_order ?? -1;

      const { data: epic, error } = await supabase
        .from("project_epics" as never)
        .insert({
          ...data,
          client_project_id: clientProjectId,
          display_order: maxOrder + 1,
        } as never)
        .select()
        .single();
      if (error) throw error;
      return epic as unknown as ProjectEpic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-epics", clientProjectId] });
      toast.success("Épico criado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useUpdateEpic(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<ProjectEpic, "title" | "description" | "color" | "status">>;
    }) => {
      const { error } = await supabase
        .from("project_epics" as never)
        .update(data as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-epics", clientProjectId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteEpic(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_epics" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-epics", clientProjectId] });
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] });
      toast.success("Épico removido!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// ─── Sprints ────────────────────────────────────────────────────────────────

export function useSprints(clientProjectId: string | undefined) {
  return useQuery({
    queryKey: ["project-sprints", clientProjectId],
    queryFn: async () => {
      if (!clientProjectId) return [];
      const { data, error } = await supabase
        .from("project_sprints" as never)
        .select("*")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as unknown as ProjectSprint[];
    },
    enabled: !!clientProjectId,
  });
}

export function useCreateSprint(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      goal?: string;
      start_date?: string;
      end_date?: string;
    }) => {
      const { data: existing } = await supabase
        .from("project_sprints" as never)
        .select("display_order")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: false })
        .limit(1);
      const maxOrder =
        (existing as unknown as { display_order: number }[])?.[0]?.display_order ?? -1;

      const { data: sprint, error } = await supabase
        .from("project_sprints" as never)
        .insert({
          ...data,
          client_project_id: clientProjectId,
          display_order: maxOrder + 1,
        } as never)
        .select()
        .single();
      if (error) throw error;
      return sprint as unknown as ProjectSprint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-sprints", clientProjectId] });
      toast.success("Sprint criada!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useUpdateSprint(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<ProjectSprint, "name" | "goal" | "start_date" | "end_date">>;
    }) => {
      const { error } = await supabase
        .from("project_sprints" as never)
        .update(data as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-sprints", clientProjectId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useStartSprint(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Only one active sprint per project — demote any other active sprint back to planned.
      await supabase
        .from("project_sprints" as never)
        .update({ status: "planned" } as never)
        .eq("client_project_id", clientProjectId)
        .eq("status", "active");

      const { data: current } = await supabase
        .from("project_sprints" as never)
        .select("start_date")
        .eq("id", id)
        .single();
      const startDate = (current as unknown as { start_date: string | null })?.start_date;

      const { error } = await supabase
        .from("project_sprints" as never)
        .update({
          status: "active",
          start_date: startDate ?? new Date().toISOString().slice(0, 10),
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-sprints", clientProjectId] });
      toast.success("Sprint iniciada!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useCompleteSprint(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: current } = await supabase
        .from("project_sprints" as never)
        .select("end_date")
        .eq("id", id)
        .single();
      const endDate = (current as unknown as { end_date: string | null })?.end_date;

      const { error } = await supabase
        .from("project_sprints" as never)
        .update({
          status: "completed",
          end_date: endDate ?? new Date().toISOString().slice(0, 10),
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-sprints", clientProjectId] });
      toast.success("Sprint concluída!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteSprint(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_sprints" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-sprints", clientProjectId] });
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] });
      toast.success("Sprint removida!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// ─── Backlog items ──────────────────────────────────────────────────────────

export function useBacklogItems(clientProjectId: string | undefined) {
  return useQuery({
    queryKey: ["project-backlog-items", clientProjectId],
    queryFn: async () => {
      if (!clientProjectId) return [];
      const { data, error } = await supabase
        .from("project_backlog_items" as never)
        .select("*")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as unknown as ProjectBacklogItem[];
    },
    enabled: !!clientProjectId,
  });
}

export interface CreateBacklogItemData {
  title: string;
  description?: string;
  item_type?: BacklogItemType;
  epic_id?: string | null;
  sprint_id?: string | null;
  story_points?: number | null;
  priority?: number;
  assigned_to?: string;
  due_date?: string;
  status?: BacklogItemStatus;
}

export function useCreateBacklogItem(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateBacklogItemData) => {
      const { data: existing } = await supabase
        .from("project_backlog_items" as never)
        .select("display_order")
        .eq("client_project_id", clientProjectId)
        .order("display_order", { ascending: false })
        .limit(1);
      const maxOrder =
        (existing as unknown as { display_order: number }[])?.[0]?.display_order ?? -1;

      const { data: item, error } = await supabase
        .from("project_backlog_items" as never)
        .insert({
          ...data,
          client_project_id: clientProjectId,
          display_order: maxOrder + 1,
        } as never)
        .select()
        .single();
      if (error) throw error;
      return item as unknown as ProjectBacklogItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] });
      toast.success("Item criado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useUpdateBacklogItem(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateBacklogItemData> }) => {
      const { error } = await supabase
        .from("project_backlog_items" as never)
        .update(data as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useReorderBacklogItems(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      updates: {
        id: string;
        display_order: number;
        status?: BacklogItemStatus;
        sprint_id?: string | null;
      }[],
    ) => {
      await Promise.all(
        updates.map(({ id, ...data }) =>
          supabase
            .from("project_backlog_items" as never)
            .update(data as never)
            .eq("id", id),
        ),
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteBacklogItem(clientProjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_backlog_items" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-backlog-items", clientProjectId] });
      toast.success("Item removido!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// ─── Subtasks ───────────────────────────────────────────────────────────────

export function useSubtasks(backlogItemId: string | undefined) {
  return useQuery({
    queryKey: ["project-subtasks", backlogItemId],
    queryFn: async () => {
      if (!backlogItemId) return [];
      const { data, error } = await supabase
        .from("project_subtasks" as never)
        .select("*")
        .eq("backlog_item_id", backlogItemId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as unknown as ProjectSubtask[];
    },
    enabled: !!backlogItemId,
  });
}

export function useCreateSubtask(backlogItemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) => {
      const { data: existing } = await supabase
        .from("project_subtasks" as never)
        .select("display_order")
        .eq("backlog_item_id", backlogItemId)
        .order("display_order", { ascending: false })
        .limit(1);
      const maxOrder =
        (existing as unknown as { display_order: number }[])?.[0]?.display_order ?? -1;

      const { error } = await supabase
        .from("project_subtasks" as never)
        .insert({ title, backlog_item_id: backlogItemId, display_order: maxOrder + 1 } as never);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-subtasks", backlogItemId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useToggleSubtask(backlogItemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_done }: { id: string; is_done: boolean }) => {
      const { error } = await supabase
        .from("project_subtasks" as never)
        .update({ is_done } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-subtasks", backlogItemId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteSubtask(backlogItemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_subtasks" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["project-subtasks", backlogItemId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}
